import { Flex, Tooltip, Typography } from "antd"
import { useTranslation } from "react-i18next"

import { budgetFormat } from "../../../../../../../utils/number/budget-format.ts"

const { Text } = Typography

interface MilestoneFinancialCellProps {
  settled: number | null
  planned: number | null
  committed: number | null
}

/**
 * Three figures, two rows. Planned is what the milestone intends, Committed is
 * what the ledger has rows for, Settled is what has actually been paid.
 *
 * Settled below Planned is the ordinary state of a job in flight, so only
 * *over*-collection is flagged. Committed is flagged in either direction: above
 * Planned means scope arrived without the milestone being updated, below means
 * the plan has not been broken into lines yet, and the user should fix both.
 */
export const MilestoneFinancialCell = ({
  settled,
  planned,
  committed
}: MilestoneFinancialCellProps) => {
  const { t } = useTranslation()
  const safeSettled = Number(settled) || 0
  const safePlanned = Number(planned) || 0
  const safeCommitted = Number(committed) || 0

  const allEmpty = !safeSettled && !safePlanned && !safeCommitted
  const settledOverPlan = !allEmpty && safeSettled > safePlanned
  const committedOffPlan = !allEmpty && safeCommitted !== safePlanned

  return (
    <Tooltip
      title={
        <Flex vertical gap={2}>
          <Text className="text-white text-xs">
            {t("milestoneMoney.settled", "Settled")}: {budgetFormat(safeSettled)}
          </Text>
          <Text className="text-white text-xs">
            {t("milestoneMoney.planned", "Planned")}: {budgetFormat(safePlanned)}
          </Text>
          <Text className="text-white text-xs">
            {t("milestoneMoney.committed", "Committed")}: {budgetFormat(safeCommitted)}
          </Text>
          {settledOverPlan && (
            <Text className="text-white text-xs">{t(
                "milestoneMoney.settledOverPlan",
                "Settled is above the planned value."
              )}</Text>
          )}
          {committedOffPlan && (
            <Text className="text-white text-xs">{t(
                "milestoneMoney.committedOffPlan",
                "Committed does not match the planned value — align the transaction lines with the plan."
              )}</Text>
          )}
        </Flex>
      }
    >
      <Flex vertical className="text-center">
        <Text
          data-testid="milestone-settled"
          className={
            settledOverPlan
              ? "text-red-500 font-bold"
              : "text-teal-600 font-medium"
          }
        >
          {budgetFormat(safeSettled)} / {budgetFormat(safePlanned)}
        </Text>
        <Text
          data-testid="milestone-committed"
          className={committedOffPlan ? "text-red-500 text-xs" : "text-xs"}
          type={committedOffPlan ? undefined : "secondary"}
        >
          {budgetFormat(safeCommitted)}
        </Text>
      </Flex>
    </Tooltip>
  )
}
